const DemoViewer = {
    config: {
        desktopBreakpoint: 1024,
        transitionDuration: 300,
        demoDelay: 500,
    },

    state: {
        prevMode: null,
    },

    elements: {},

    init() {
        this.cache();

        if (!this.elements.demo.iframeContainer) return;

        this.bindEvents();

        this.ui.handleScroll();

        this.ui.updateDefaultMode();
    },

    cache() {
        this.elements = {
            popup: {
                container: document.getElementById('demo-alert-popup'),
                closeBtn: document.getElementById('close-popup-btn'),
            },

            demo: {
                container: document.getElementById('demo-container'),
                iframeContainer: document.getElementById('demo-iframe-container'),

                detailsBar: document.getElementById('details-bar'),
                detailsBarBlur: document.getElementById('details-bar-blur'),

                openBtn: document.getElementById('view-demo'),
                shimmerBtn: document.querySelector('[data-demo-store-target="shimmer"]'),

                closeBtn: document.querySelector('.close-demo'),
                tryTheme: document.querySelector('.try-theme'),
            },

            mode: {
                wrapper: document.getElementById('mode-full'),
                switcher: document.getElementById('mode-switcher'),

                fullscreenBtn: document.querySelector(
                    '#mode-switcher button[data-active="full-screen"]'
                ),

                buttons: [
                    ...document.querySelectorAll(
                        '#mode-switcher .switch-button button'
                    ),
                ],
            },

            header: document.querySelector('.template-header'),

            body: document.body,
        };

    },

    bindEvents() {
        const { popup, demo, mode } = this.elements;

        demo.openBtn?.addEventListener('click', () => this.ui.open());
        demo.shimmerBtn?.addEventListener('click', () => this.ui.open());
        demo.closeBtn?.addEventListener('click', () => this.ui.close());

        mode.fullscreenBtn?.addEventListener('click', () => {
            this.ui.toggleFullscreen();
        });

        popup.closeBtn?.addEventListener('click', () => {
            popup.container.style.display = 'none';
        });

        window.addEventListener('click', (e) => {
            if (e.target === popup.container) {
                popup.container.style.display = 'none';
            }
        });

        mode.buttons.forEach(button => {
            button.addEventListener('click', () => {
                this.ui.setActive(button);
            });
        });

        window.addEventListener('resize', () => {
            if (!demo.iframeContainer.classList.contains('demo-active')) {
                this.ui.updateDefaultMode();
            }
        });

        window.addEventListener('scroll',
            () => this.ui.handleScroll(), {
            passive: true,
        }
        );
    },

    ui: {

        open() {
            const { demo, mode, popup } = DemoViewer.elements;
            const iframe = demo.iframeContainer?.querySelector('iframe');
            const iframeSrc = iframe?.getAttribute('src');

            if (!iframeSrc || iframeSrc.trim() === '' || iframeSrc === 'about:blank') {
                popup.container.style.display = 'flex';
                return;
            }

            mode.wrapper.classList.remove('hidden');
            mode.wrapper.classList.add('flex');

            demo.closeBtn?.classList.remove('opacity-0');
            demo.tryTheme?.classList.remove('opacity-0');

            mode.fullscreenBtn?.classList.remove('hidden');

            demo.iframeContainer?.classList.add('demo-active');

            mode.fullscreenBtn?.click();

            setTimeout(() => {
                demo.detailsBar?.classList.add('demo-active');
            }, DemoViewer.config.demoDelay);

        },

        close() {
            const { demo, mode, body } = DemoViewer.elements;

            demo.closeBtn?.classList.add('opacity-0');
            demo.tryTheme?.classList.add('opacity-0');
            mode.fullscreenBtn?.classList.add('hidden');
            demo.detailsBar?.classList.remove('demo-active');

            demo.iframeContainer?.classList.remove(
                'demo-active',
                'fullscreen-mode'
            );

            demo.container?.classList.remove(
                'demo-active',
                'fullscreen-mode'
            );

            mode.switcher?.classList.remove('fix-fullscreen');

            body.style.overflow = '';
            body.classList.remove('demo-active');

            mode.wrapper?.classList.remove('flex');
            mode.wrapper?.classList.add('hidden');

            const defaultMode = DemoViewer.helpers.getDefaultMode();

            const defaultButton = mode.buttons.find(
                button => button.dataset.active === defaultMode
            );

            if (defaultButton) {
                DemoViewer.ui.setActive(defaultButton, true);
            }
        },

        toggleFullscreen() {
            const { demo, mode, body } = DemoViewer.elements;

            const isFullscreen = demo.iframeContainer.classList.contains('fullscreen-mode');

            if (!isFullscreen) {

                const activeModeBtn = mode.buttons
                    .filter(btn => btn.dataset.active !== 'full-screen')
                    .find(btn => btn.classList.contains('active'));

                DemoViewer.state.prevMode = activeModeBtn?.dataset.active || null;

                demo.iframeContainer.classList.add('fullscreen-mode');
                demo.container.classList.add('fullscreen-mode');

                mode.switcher.classList.add('fix-fullscreen');

                body.style.overflow = 'hidden';

                body.classList.add('demo-active');

                mode.fullscreenBtn.classList.add(
                    'close-full',
                    'active'
                );

            } else {

                demo.iframeContainer.classList.remove('fullscreen-mode');
                demo.container.classList.remove('fullscreen-mode');

                mode.switcher.classList.remove('fix-fullscreen');

                body.style.overflow = '';
                body.classList.remove('demo-active');

                mode.fullscreenBtn.classList.remove(
                    'close-full',
                    'active'
                );

                const prevButton = mode.buttons.find(
                    btn => btn.dataset.active === DemoViewer.state.prevMode
                );

                if (prevButton) {
                    DemoViewer.ui.setActive(prevButton, true);
                }
            }
        },

        setActive(buttonToActivate, ignoreFullscreen = true) {
            const { demo } = DemoViewer.elements;
            const iframe = demo.iframeContainer?.querySelector('iframe');
            const isDemoActive = demo.iframeContainer.classList.contains('demo-active');

            if (!isDemoActive || !document.startViewTransition) {
                DemoViewer.ui.applyMode(buttonToActivate, ignoreFullscreen);
                return;
            }

            if (iframe) iframe.style.viewTransitionName = 'demo-iframe';

            document.startViewTransition(() => {
                DemoViewer.ui.applyMode(
                    buttonToActivate,
                    ignoreFullscreen
                );
            });

            setTimeout(() => {
                iframe?.style.removeProperty('view-transition-name');
            }, DemoViewer.config.transitionDuration);

        },

        applyMode(buttonToActivate, ignoreFullscreen = true) {
            const { demo, mode } = DemoViewer.elements;

            const fullscreenButton = mode.buttons.find(
                btn => btn.dataset.active === 'full-screen'
            );

            if (
                buttonToActivate.dataset.active !== 'full-screen' &&
                fullscreenButton
            ) {
                fullscreenButton.classList.remove('active');
                fullscreenButton.setAttribute('aria-pressed', 'false');
            }

            mode.buttons.forEach(button => {

                const isActive = button === buttonToActivate;
                const modeName = button.dataset.active;

                if (
                    ignoreFullscreen &&
                    modeName === 'full-screen'
                ) return;

                button.classList.toggle('active', isActive);

                button.setAttribute(
                    'aria-pressed',
                    isActive ? 'true' : 'false'
                );

                if (!isActive) return;

                demo.iframeContainer.classList.remove(
                    'desktop-mode',
                    'mobile-mode',
                    'fullscreen-mode'
                );

                demo.container.classList.remove(
                    'desktop-mode',
                    'mobile-mode',
                    'fullscreen-mode'
                );

                demo.iframeContainer.classList.add(modeName);

                demo.container.classList.add(modeName);
            });

        },

        handleScroll() {
            const { demo } = DemoViewer.elements;

            if (!demo.detailsBar) return;

            const isDesktop = window.innerWidth >= DemoViewer.config.desktopBreakpoint;
            if (!isDesktop) {
                demo.detailsBar.classList.remove('active');
                return;
            }

            const rect = demo.detailsBar.getBoundingClientRect();

            if (rect.top <= 76) {
                demo.detailsBar.classList.add('active');
            } else {
                demo.detailsBar.classList.remove('active');
            }
        },

        updateDefaultMode() {
            const { mode } = DemoViewer.elements;
            const defaultMode = DemoViewer.helpers.getDefaultMode();
            const button = mode.buttons.find(
                btn => btn.dataset.active === defaultMode
            );

            if (button) {
                DemoViewer.ui.setActive(button);
            }
        },
    },

    helpers: {
        getDefaultMode() {
            return window.innerWidth >= DemoViewer.config.desktopBreakpoint
                ? 'desktop-mode'
                : 'mobile-mode';
        }
    }
};

const Cursor = {
    elements: {},

    init() {
        this.cache();
        if (!this.elements.zone || !this.elements.cursor) return;
        this.bindEvents();
    },

    cache() {
        this.elements = {
            zone: document.querySelector('[data-controller="monorail-click-tracking"]'),
            cursor: document.querySelector('[data-demo-store-target="cursor"]'),
        };
    },

    bindEvents() {
        const { zone } = this.elements;

        zone.addEventListener('mouseenter', () => this.show());
        zone.addEventListener('mouseleave', () => this.hide());
        zone.addEventListener('mousemove', e => this.move(e));
    },

    show() {
        this.elements.cursor.style.opacity = '1';
        this.elements.cursor.style.transform = 'none';
    },

    hide() {
        this.elements.cursor.style.opacity = '0';
    },

    move(event) {
        const { zone, cursor } = this.elements;
        const rect = zone.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        cursor.style.transform = `translate(${x}px, ${y}px) rotate(0deg) scale(1)`;
    },
};

const VideoTutorial = {

    player: null,
    playerReady: false,

    thumbsSwiper: null,

    stepSlides: [],
    stepTimes: [],
    currentActiveIndex: -1,

    trackingTimer: null,

    hasAutoplayed: false,
    shouldAutoplay: false,

    init() {

        this.cache();

        if (!this.installEl || !this.videoEl) return;

        this.initSwiper();
        this.collectSteps();

        this.observeVideo();

    },

    cache() {
        const install_video = document.getElementById('install-video');
        if (!install_video) return;

        this.installEl = document.getElementById('install');
        this.videoEl = install_video

        // Wrapper có kích thước thật
        this.videoWrapper = this.videoEl.parentElement;

    },

    observeVideo() {

        const PRELOAD_OFFSET = '2000px 0px';
        const AUTOPLAY_THRESHOLD = 0.7; // 30% video xuất hiện thì autoplay

        const observer = new IntersectionObserver(([entry]) => {

            // Load player trước
            if (!this.player && entry.isIntersecting) {
                this.loadYoutubeApi().then(() => this.createPlayer());
            }

            // Autoplay đúng 1 lần
            if (
                this.hasAutoplayed ||
                entry.intersectionRatio < AUTOPLAY_THRESHOLD
            ) {
                return;
            }

            this.hasAutoplayed = true;

            if (this.playerReady) {
                this.player.mute();
                this.player.playVideo();
            } else {
                this.shouldAutoplay = true;
            }

            // Không cần theo dõi nữa
            observer.disconnect();

        }, {
            rootMargin: PRELOAD_OFFSET,
            threshold: [0, AUTOPLAY_THRESHOLD]
        });

        observer.observe(this.videoWrapper);

    },

    loadYoutubeApi() {

        return new Promise(resolve => {

            if (typeof YT !== 'undefined' && YT.Player) {
                resolve();
                return;
            }

            if (document.getElementById('youtube-api')) {

                const wait = setInterval(() => {

                    if (typeof YT !== 'undefined' && YT.Player) {

                        clearInterval(wait);

                        resolve();

                    }

                }, 50);

                return;

            }

            const tag = document.createElement('script');

            tag.id = 'youtube-api';
            tag.src = 'https://www.youtube.com/iframe_api';

            window.onYouTubeIframeAPIReady = resolve;

            document.head.appendChild(tag);

        });

    },

    createPlayer() {

        if (this.player) return;

        this.player = new YT.Player(this.videoEl, {

            width: '100%',
            height: '100%',

            videoId: this.videoEl.dataset.videoId,

            playerVars: {
                autoplay: 0,
                controls: 1,
                rel: 0,
                modestbranding: 1
            },

            events: {

                onReady: () => {

                    this.playerReady = true;

                    this.startTracking();

                    if (this.shouldAutoplay) {

                        this.player.mute();
                        this.player.playVideo();

                        this.shouldAutoplay = false;

                    }

                },

                onStateChange: (e) => {

                    if (e.data === YT.PlayerState.ENDED) {

                        this.currentActiveIndex = 0;
                        this.updateActiveThumb(0);

                    }

                }

            }

        });

    },

    initSwiper() {

        this.thumbsSwiper = new Swiper('.install-thumbs', {
            direction: 'vertical',
            slidesPerView: 3,
            spaceBetween: 24,
            freeMode: true
        });

    },

    collectSteps() {

        this.stepSlides = [];
        this.stepTimes = [];

        document
            .querySelectorAll('.install-thumbs .swiper-slide')
            .forEach(slide => {

                this.stepSlides.push(slide);

                this.stepTimes.push(
                    Number(slide.dataset.start) || 0
                );

            });

    },

    startTracking() {

        if (this.trackingTimer) return;

        this.trackingTimer = setInterval(() => {

            if (
                !this.playerReady ||
                this.player.getPlayerState() !== YT.PlayerState.PLAYING
            ) {
                return;
            }

            const currentTime = this.player.getCurrentTime();

            let activeIndex = this.stepTimes.length - 1;

            for (let i = 0; i < this.stepTimes.length; i++) {

                if (currentTime < this.stepTimes[i]) {

                    activeIndex = Math.max(i - 1, 0);

                    break;

                }

            }

            if (activeIndex !== this.currentActiveIndex) {

                this.currentActiveIndex = activeIndex;

                this.updateActiveThumb(activeIndex);

            }

        }, 100);

    },

    updateActiveThumb(index) {

        this.stepSlides.forEach((slide, i) => {

            slide.classList.toggle(
                'swiper-slide-thumb-active',
                i === index
            );

        });

        this.thumbsSwiper?.slideTo(index);

    }

};

const Navigation = {
    config: {
        desktopBreakpoint: 1024,
        offset: 100,
    },

    state: {
        lastScrollY: 0,
    },

    elements: {},

    init() {
        this.cache();
        if (!this.elements.sections.length) return;
        this.state.lastScrollY = window.scrollY;
        this.bindEvents();
        this.onScroll();
    },

    cache() {
        this.elements = {
            sections: [
                ...document.querySelectorAll('.frame-item')
            ],

            navItems: [
                ...document.querySelectorAll('.navigation li')
            ],

            navigator: document.querySelector('.navigation ul'),

            featured: document.querySelector('.frame-content'),
        };

    },

    bindEvents() {
        window.addEventListener(
            'scroll',
            () => this.onScroll(),
            { passive: true }
        );
    },

    onScroll() {
        this.updateActiveSection();

        this.updateCompactMode();

        this.state.lastScrollY = Math.max(
            window.scrollY,
            0
        );
    },

    updateActiveSection() {
        const {
            sections,
            navItems,
            navMap
        } = this.elements;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            const navItem = navMap.get(section.id);

            if (!navItem) return;

            const isActive =
                rect.top <= this.config.offset &&
                rect.bottom > this.config.offset;

            if (!isActive) return;

            navItems.forEach(item => {
                item.classList.remove('active');
                item.style.setProperty('--progress', 0);
            });

            navItem.classList.add('active');

            const scrollPassed = Math.min(
                Math.max(this.config.offset - rect.top, 0),
                rect.height
            );

            navItem.style.setProperty('--progress', scrollPassed / rect.height);

        });

    },

    updateCompactMode() {
        const { featured, navigator } = this.elements;

        if (
            !featured ||
            !navigator ||
            window.innerWidth < this.config.desktopBreakpoint
        ) {
            return;
        }

        const rect = featured.getBoundingClientRect();

        const isInside =
            rect.top <= this.config.offset &&
            rect.bottom > this.config.offset;

        if (!isInside) return;

        const direction =
            window.scrollY > this.state.lastScrollY
                ? 'down'
                : 'up';

        navigator.classList.toggle(
            'compact',
            direction === 'up'
        );

    },

    cache() {

        const sections = [...document.querySelectorAll('.frame-item')];
        const navItems = [...document.querySelectorAll('.navigation li')];
        const navMap = new Map();

        sections.forEach(section => {
            navMap.set(
                section.id,
                document.querySelector(
                    `.navigation li[data-item="${section.id}"]`
                )
            );
        });

        this.elements = {
            sections,
            navItems,
            navMap,
            navigator: document.querySelector('.navigation ul'),
            featured: document.querySelector('.frame-content'),
        };

    },
};

const Accordion = {
    config: {
        transitionDuration: 250,
    },

    elements: {},

    init() {
        this.cache();
        if (!this.elements.items.length) return;
        this.prepare();
        this.bindEvents();
    },

    cache() {
        this.elements = {
            items: [...document.querySelectorAll('.accordion-details')]
        };
    },

    prepare() {
        this.elements.items.forEach(details => {
            const summary = details.querySelector('summary');
            const content = details.querySelector('.accordion-details__content');

            const baseHeight =
                summary.offsetHeight +
                parseInt(getComputedStyle(details).paddingTop) +
                parseInt(getComputedStyle(details).paddingBottom);

            details.dataset.baseHeight = baseHeight;

            if (!details.hasAttribute('open')) {
                details.style.height = `${baseHeight}px`;
                content.style.opacity = 0;
                content.style.pointerEvents = 'none';
            }
        });
    },

    bindEvents() {
        this.elements.items.forEach(details => {
            const summary = details.querySelector('summary');
            summary.addEventListener('click', e => {
                e.preventDefault();
                this.toggle(details);
            });
        });
    },

    toggle(details) {
        const content = details.querySelector('.accordion-details__content');

        if (!details.hasAttribute('open')) {
            this.elements.items.forEach(item => {
                if (item !== details && item.hasAttribute('open')) {
                    this.close(item);
                }
            });

            this.open(details);

        } else {
            this.close(details);
        }
    },

    open(details) {
        const content = details.querySelector('.accordion-details__content');
        const baseHeight = parseInt(details.dataset.baseHeight);

        details.setAttribute('open', '');
        details.setAttribute('aria-expanded', 'true');

        const contentHeight = content.scrollHeight;

        details.style.height = `${baseHeight}px`;

        content.style.opacity = 0;
        content.style.pointerEvents = 'none';

        requestAnimationFrame(() => {
            details.style.height = `${baseHeight + contentHeight}px`;
            content.style.opacity = 1;
            content.style.pointerEvents = 'auto';
        });

        details.addEventListener('transitionend',
            function handler(e) {
                if (e.propertyName !== 'height') return;
                details.style.height = 'auto';
                details.removeEventListener('transitionend', handler);
            }
        );
    },

    close(details) {
        const content = details.querySelector('.accordion-details__content');
        const baseHeight = parseInt(details.dataset.baseHeight);
        const fullHeight = details.scrollHeight;

        details.style.height = `${fullHeight}px`;
        details.setAttribute('aria-expanded', 'false');

        requestAnimationFrame(() => {
            details.style.transition = 'height 0.25s ease';
            content.style.transition = 'opacity 0.2s ease';
            details.style.height = `${baseHeight}px`;
            content.style.opacity = 0;
            content.style.pointerEvents = 'none';
        });

        details.addEventListener(
            'transitionend',
            function handler(e) {
                if (e.propertyName !== 'height') return;
                details.removeAttribute('open');
                details.removeEventListener('transitionend', handler);
            }
        );
    },
};

const Swipers = {

    state: {
        productRelated: null,
        blogRelated: null,
    },

    init() {
        this.initProductRelated();

        this.initBlogRelated();
    },

    initProductRelated() {
        const container = document.querySelector('#js-swiper-product-related');

        if (!container) return;

        this.state.productRelated = new Swiper(container, {
            slidesPerView: 1.15,
            spaceBetween: 16,
            loop: true,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            navigation: {
                nextEl: '.product-related .swiper-button-next',
                prevEl: '.product-related .swiper-button-prev',
            },
            pagination: {
                el: '#js-swiper-product-related .swiper-pagination',
                clickable: true,
            },
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                },
                992: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                },
                1200: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                },
            },
        });
    },

    initBlogRelated() {
        const container = document.querySelector('#js-swiper-blog-related');

        if (!container) return;

        this.state.blogRelated = new Swiper(container, {
            slidesPerView: 1.15,
            spaceBetween: 16,
            loop: true,
            centeredSlides: false,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            navigation: {
                nextEl: '#js-swiper-blog-related .swiper-button-next',
                prevEl: '#js-swiper-blog-related .swiper-button-prev',
            },
            pagination: {
                el: '#js-swiper-blog-related .swiper-pagination',
                clickable: true,
            },
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                },
                992: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                },
                1200: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                },
            },
        });
    },

};


document.addEventListener('DOMContentLoaded', () => {
    DemoViewer.init();

    Cursor.init();

    VideoTutorial.init();

    Accordion.init();

    Swipers.init();

    Navigation.init();
});